import {ChangeEventHandler, FocusEventHandler, useCallback, useEffect, useMemo, useState} from 'react';
import {useDeleteFromCartMutation, useGetCartQuery, useUpdateCartMutation} from 'modules/cart/services';
import _debounce from 'lodash/debounce';
import {ProductOfferShort} from 'models';
import {useCartEventAnalytics} from 'modules/products/hooks/gtag/useCartEventAnalytics';
import {IAnalyticsMeta} from 'core/types';

interface IProps extends Pick<ProductOfferShort, 'id' | 'offerId'> {
    multiple?: number;
    analytics?: IAnalyticsMeta;
}

export const useCartProduct = ({id, offerId, multiple = 1, analytics}: IProps) => {
    const cartEventGtag = useCartEventAnalytics();

    const [updateCartItem] = useUpdateCartMutation();
    const [deleteFromCart] = useDeleteFromCartMutation();
    const {data: cartData} = useGetCartQuery({mini: true});

    const product = useMemo(() => {
        return cartData?.products.find((item) => item.offerId === offerId);
    }, [cartData?.products, offerId]);

    const productCount = useMemo(() => {
        return cartData?.products.find((item) => item.offerId === offerId)?.cartData?.quantity ?? 0;
    }, [cartData?.products, offerId]);

    const [localCount, setLocalCount] = useState<number>(productCount);

    useEffect(() => {
        setLocalCount(productCount);
    }, [productCount]);

    const safeMultiple = Math.max(1, multiple);

    const debouncedUpdate = useMemo(
        () =>
            _debounce((quantity: number) => {
                if (0 === quantity) {
                    deleteFromCart({id})
                        .unwrap()
                        .then(() => cartEventGtag(product as any, 0, analytics));
                } else {
                    updateCartItem({id, quantity})
                        .unwrap()
                        .then(() => cartEventGtag(product as any, quantity, analytics));
                }
            }, 500),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deleteFromCart, id, product, updateCartItem],
    );

    useEffect(() => {
        return () => {
            debouncedUpdate.cancel();
        };
    }, [debouncedUpdate]);

    const handleAdd: () => void = useCallback(() => {
        const newCount = (localCount || 0) + safeMultiple;
        setLocalCount(newCount);
        updateCartItem({id, quantity: newCount})
            .unwrap()
            .then(() => cartEventGtag(product as any, newCount, analytics));
    }, [localCount, safeMultiple, updateCartItem, id, cartEventGtag, product, analytics]);

    const handleRemove: () => void = useCallback(() => {
        const newCount = Math.max(0, (localCount || 0) - safeMultiple);
        setLocalCount(newCount);
        if (0 === newCount) {
            deleteFromCart({id})
                .unwrap()
                .then(() => cartEventGtag(product as any, 0, analytics));
        } else {
            updateCartItem({id, quantity: newCount})
                .unwrap()
                .then(() => cartEventGtag(product as any, newCount, analytics));
        }
    }, [localCount, safeMultiple, deleteFromCart, id, cartEventGtag, product, analytics, updateCartItem]);

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const {value, min, max} = e.target;
            if ('' === value) {
                setLocalCount(0);
                debouncedUpdate(0);
                return;
            }

            if (!/^\d+$/.test(value)) {
                return;
            }
            const num = Math.max(Number(min), Math.min(Number(max), Number(value)));
            setLocalCount(num);
            debouncedUpdate(num);
        },
        [debouncedUpdate],
    );

    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(() => {
        debouncedUpdate.flush();
    }, [debouncedUpdate]);

    return {
        cartProductCount: localCount,
        handleCartCountAddClick: handleAdd,
        handleCartCountRemoveClick: handleRemove,
        handleCartCountChange: handleChange,
        handleCartCounterInputBlur: handleBlur,
    };
};
